import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './services/AuthContext';
import { ConfigProvider } from './context/ConfigProvider';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { CurrencyProvider } from './services/CurrencyContext';
import { AuthModalProvider } from './context/AuthModalContext';
import { CountryProvider, useCountry } from './context/CountryContext';
import { VerifyAgentProvider } from './context/VerifyAgentContext';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import BrokerLayout from './layouts/BrokerLayout';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import { Sparkles } from 'lucide-react';

// --- Pages ---
const BrokerDashboardPage = lazy(() => import('./pages/BrokerDashboardPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const PipelinePage = lazy(() => import('./pages/PipelinePage'));
const CILsPage = lazy(() => import('./pages/CILsPage'));
const SalesPage = lazy(() => import('./pages/SalesPage'));
const CommissionsPage = lazy(() => import('./pages/CommissionsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const InsightsPage = lazy(() => import('./pages/InsightsPage'));
const InsightsTopPage = lazy(() => import('./pages/InsightsTopPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const CompanySettingsPage = lazy(() => import('./pages/CompanySettings'));

const PartnerLandingPage = lazy(() => import('./pages/PartnerLandingPage'));
const PartnerLoginPage = lazy(() => import('./pages/PartnerLoginPage'));
const PartnerSignupPage = lazy(() => import('./pages/PartnerSignupPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const CheckEmailPage = lazy(() => import('./pages/CheckEmailPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const RequestPasswordResetPage = lazy(() => import('./pages/RequestPasswordResetPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const PartnerPropertyDetailPage = lazy(() => import('./pages/PartnerPropertyDetailPage'));
const PartnerCompoundDetailPage = lazy(() => import('./pages/PartnerCompoundDetailPage'));
const PartnerDeveloperDetailPage = lazy(() => import('./pages/PartnerDeveloperDetailPage'));
const PartnerAreaDetailPage = lazy(() => import('./pages/PartnerAreaDetailPage'));

// --- Placeholders for mobile-parallel screens ---
const CoPilotPage = () => (
    <div className="bg-slate-900 p-12 rounded-[40px] text-center text-white relative overflow-hidden shadow-xl border border-white/5">
        <div className="absolute top-0 right-0 p-8 opacity-10 rotate-12">
            <Sparkles size={120} />
        </div>
        <div className="relative z-10">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                <Sparkles className="w-8 h-8 text-white" strokeWidth={1.5} />
            </div>
            <h2 className="text-3xl font-bold mb-4 tracking-normal lowercase text-blue-500">Vertex Co-Pilot</h2>
            <p className="opacity-60 font-medium max-w-md mx-auto">AI analysis engine is calibrating for widescreen projection. Real-time property advisory and document parsing modules under heavy training.</p>
        </div>
    </div>
);

// --- Helper Components ---
const PageLoader = () => (
  <div className="flex h-screen w-full items-center justify-center bg-white dark:bg-[#0A0A0A]">
    <div className="flex flex-col items-center gap-4">
        <div className="h-12 w-12 animate-spin rounded-full border-[3px] border-slate-200 dark:border-white/10 border-t-slate-900 dark:border-t-white"></div>
        <span className="text-[10px] font-bold  tracking-[0.3em] text-slate-400 animate-pulse">Syncing Pro Hub</span>
    </div>
  </div>
);

import { useAuth } from './services/AuthContext';

const ProtectedRoute = () => {
  const { isLoggedIn, isLoading } = useAuth();
  
  if (isLoading) return <PageLoader />;
  
  // ✅ FIX: Redirect to root to handle localized redirection back to login
  if (!isLoggedIn) return <Navigate to="/" replace />;
  
  return <Outlet />;
};

const RootRedirect = () => {
    const { activeCountry } = useCountry();
    if (!activeCountry) return <PageLoader />;
    return <Navigate to={`/${activeCountry.code.toLowerCase()}`} replace />;
};

function App() {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || "YOUR_GOOGLE_CLIENT_ID_HERE"}>
        <AuthProvider>
          <AuthModalProvider>
            <VerifyAgentProvider>
              <ConfigProvider>
                <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                  <CountryProvider>
                    <CurrencyProvider>
                      <Helmet>
                        <title>
                          {import.meta.env.VITE_APP_MODE === 'erealty' 
                            ? 'eRealty Hub | Institutional Inventory' 
                            : 'LAQY PRO | Institutional Broker Workspace'}
                        </title>
                        <meta name="description" content={
                          import.meta.env.VITE_APP_MODE === 'erealty'
                            ? 'The ultimate real estate inventory hub for institutional partners.'
                            : 'Professional real estate workstation for vetted partners.'
                        } />
                      </Helmet>
                      <ErrorBoundary>
                        <ScrollToTop />
                        <Suspense fallback={<PageLoader />}>
                          <Routes>
                            <Route path="/" element={<RootRedirect />} />
                            
                            <Route path="/:countryCode">
                              {/* Backward compatibility with previous Unified build paths */}
                              <Route path="broker/login" element={<Navigate to="../partners/login" replace />} />
                              <Route path="broker/signup" element={<Navigate to="../partners/signup" replace />} />
                              <Route path="broker/*" element={<Navigate to="../partners" replace />} />

                              <Route path="partners/landing" element={<PartnerLandingPage />} />
                              <Route path="partners/login" element={<PartnerLoginPage />} />
                              <Route path="partners/signup" element={<PartnerSignupPage />} />
                              
                              {/* Account Verification & Recovery */}
                              <Route path="check-email" element={<CheckEmailPage />} />
                              <Route path="verify-email" element={<VerifyEmailPage />} />
                              <Route path="partners/reset-password-request" element={<RequestPasswordResetPage />} />
                              <Route path="partners/reset-password" element={<ResetPasswordPage />} />
                              
                              <Route path="privacy" element={<PrivacyPage />} />
                              <Route path="terms" element={<TermsPage />} />

                              <Route element={<ProtectedRoute />}>
                                /* Standard Broker Partners Portal Routes */
                                <Route path="partners" element={<BrokerLayout><Outlet /></BrokerLayout>}>
                                  <Route path="dashboard" element={<BrokerDashboardPage />} />
                                  <Route path="inventory" element={<InventoryPage />} />
                                  <Route path="property/:id" element={<PartnerPropertyDetailPage />} />
                                  <Route path="compound/:id" element={<PartnerCompoundDetailPage />} />
                                  <Route path="developer/:id" element={<PartnerDeveloperDetailPage />} />
                                  <Route path="area/:id" element={<PartnerAreaDetailPage />} />
                                  <Route path="terms" element={<TermsPage />} />
                                   <Route path="pipeline" element={<PipelinePage />} />
                                   <Route path="pipeline/leads" element={<PipelinePage />} />
                                   <Route path="pipeline/cils" element={<CILsPage />} />
                                   <Route path="pipeline/reservations" element={<PipelinePage />} />
                                   <Route path="pipeline/history" element={<PipelinePage />} />
                                  <Route path="cils" element={<CILsPage />} />
                                  <Route path="sales" element={<SalesPage />} />
                                  <Route path="commissions" element={<CommissionsPage />} />
                                  <Route path="co-pilot" element={<CoPilotPage />} />
                                  <Route path="settings" element={<ProfilePage />} />
                                  
                                  {/* Corporate Only Routes */}
                                  <Route element={<RoleProtectedRoute allowedRoles={['COMPANY_OWNER', 'ADMIN']} />}>
                                    <Route path="sales/corporate" element={<SalesPage />} />
                                    <Route path="commissions/team" element={<CommissionsPage />} />
                                    <Route path="insights" element={<InsightsPage />} />
                                    <Route path="insights/grow" element={<InsightsPage />} />
                                    <Route path="insights/top" element={<InsightsTopPage />} />
                                    <Route path="team" element={<TeamPage />} />
                                    <Route path="settings/corporate" element={<CompanySettingsPage />} />
                                  </Route>
                                  <Route path="" element={<Navigate to="dashboard" replace />} />
                                </Route>
                              </Route>
                              
                              {/* Fallback to portal default */}
                              <Route path="" element={<Navigate to="partners/landing" replace />} />
                            </Route>

                            <Route path="*" element={<Navigate to="/" replace />} />
                          </Routes>
                        </Suspense>
                      </ErrorBoundary>
                    </CurrencyProvider>
                  </CountryProvider>
                </Router>
              </ConfigProvider>
            </VerifyAgentProvider>
          </AuthModalProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App;
